import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Stack
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon, 
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../config/firebase';
import { RazorpayResponse } from '../types/razorpay';

// Define types for better TypeScript support
interface LinkData {
  amount: number;
  serviceName: string;
  description: string;
  patientName?: string;
  patientEmail?: string;
  patientPhone?: string;
  status: string;
  createdAt: any;
  expiryDate?: any;
}

interface CustomerDetails {
  name: string;
  email: string;
  phone: string;
}

// Razorpay configuration
const RAZORPAY_CONFIG = {
  keyId: 'rzp_live_RkMmphV9lefW0Y',
  name: 'KBR Life Care Hospital',
  image: '/assets/kbr-logo.png',
  theme: {
    color: '#1976d2'
  }
};

const PaymentLinkPage: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const navigate = useNavigate();
  const [linkData, setLinkData] = useState<LinkData | null>(null);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>({
    name: '',
    email: '',
    phone: ''
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('upi');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [paymentSuccess, setPaymentSuccess] = useState<boolean>(false);

  const loadPaymentLink = useCallback(async () => {
    if (!linkId) return;
    
    try {
      setLoading(true);
      const linkDoc = await getDoc(doc(db, 'payment_links', linkId));
      
      if (!linkDoc.exists()) {
        setError('Payment link not found or has expired');
        return;
      }

      const data = linkDoc.data() as LinkData;
      
      // Check if link has expired
      if (data.expiryDate && new Date() > data.expiryDate.toDate()) {
        setError('This payment link has expired');
        return;
      }
      
      // Check if link is already used
      if (data.status === 'used') {
        setError('This payment link has already been used');
        return;
      }

      setLinkData(data);
      
      // Pre-fill customer details if available
      if (data.patientName) {
        setCustomerDetails(prev => ({
          ...prev,
          name: data.patientName || '',
          email: data.patientEmail || '',
          phone: data.patientPhone || ''
        }));
      }
      
    } catch (error) {
      console.error('Error loading payment link:', error);
      setError('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  }, [linkId]);

  useEffect(() => {
    loadPaymentLink();
  }, [loadPaymentLink]);

  const handlePayment = async () => {
    if (!linkData || !customerDetails.name || !customerDetails.email || !customerDetails.phone) {
      setError('Please fill all required fields');
      return;
    }

    // Validate phone number
    if (customerDetails.phone.length !== 10) {
      setError('Phone number must be exactly 10 digits');
      return;
    }
    if (!/^[6-9]\d{9}$/.test(customerDetails.phone)) {
      setError('Invalid phone number format');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Load Razorpay script
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      document.body.appendChild(script);

      script.onload = async () => {
        // Create Razorpay order
        const orderData = {
          amount: linkData.amount,
          currency: 'INR',
          receipt: `link_${linkId}_${Date.now()}`,
          payment_capture: 1
        };

        // For demo purposes, creating a mock order
        // In production, this should be done on your backend
        const order = {
          id: `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          amount: linkData.amount,
          currency: 'INR',
          receipt: orderData.receipt
        };

        // Razorpay options
        const options = {
          key: RAZORPAY_CONFIG.keyId,
          amount: linkData.amount,
          currency: 'INR',
          name: RAZORPAY_CONFIG.name,
          description: linkData.description,
          order_id: order.id,
          prefill: {
            name: customerDetails.name,
            email: customerDetails.email,
            contact: customerDetails.phone
          },
          theme: RAZORPAY_CONFIG.theme,
          method: getMethodConfig(),
          handler: function (response: RazorpayResponse) {
            handlePaymentSuccess(response);
          },
          modal: {
            ondismiss: function () {
              setLoading(false);
            }
          }
        };

        // Open Razorpay checkout
        const rzp = new window.Razorpay(options);
        rzp.open();
      };

    } catch (error) {
      console.error('Payment error:', error);
      setError('Payment failed. Please try again.');
      setLoading(false);
    }
  };

  const getMethodConfig = () => {
    const config: Record<string, boolean> = {
      netbanking: false,
      card: false,
      wallet: false,
      upi: false
    };
    
    if (selectedPaymentMethod in config) {
      config[selectedPaymentMethod] = true;
    }
    return config;
  };

  const handlePaymentSuccess = async (response: RazorpayResponse) => {
    if (!linkData) return;
    
    try {
      console.log('Payment successful:', response);

      // Save payment details to Firebase
      const paymentData = {
        // Payment details
        orderId: response.razorpay_order_id,
        paymentId: response.razorpay_payment_id,
        signature: response.razorpay_signature,
        amount: linkData.amount,
        currency: 'INR',
        status: 'success',
        method: selectedPaymentMethod,
        
        // Customer details
        patientName: customerDetails.name,
        patientEmail: customerDetails.email,
        patientPhone: customerDetails.phone,
        
        // Service details
        serviceName: linkData.serviceName,
        description: linkData.description,
        
        // Timestamps
        createdAt: new Date(),
        paymentDate: new Date()
      };

      // Add to payments collection
      await addDoc(collection(db, 'payments'), paymentData);

      // Update link status to used
      if (linkId) {
        await updateDoc(doc(db, 'payment_links', linkId), {
          status: 'used',
          usedAt: new Date(),
          paymentId: response.razorpay_payment_id
        });
      }

      setPaymentSuccess(true);
      setLoading(false);

      // Redirect to success page after 3 seconds
      setTimeout(() => {
        navigate('/payment-success');
      }, 3000);

    } catch (error) {
      console.error('Error saving payment:', error);
      setError('Payment processed but failed to save details');
      setLoading(false);
    }
  };

  const formatAmount = (amountInPaise: number): string => {
    return (amountInPaise / 100).toFixed(2);
  };

  if (loading && !linkData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (paymentSuccess) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom color="success.main">
            Payment Successful!
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Your payment of ₹{linkData ? formatAmount(linkData.amount) : '0'} has been processed successfully.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting to receipt page...
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (!linkData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          Loading payment details...
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
        Complete Your Payment
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Payment Summary */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payment Summary
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Service
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {linkData.serviceName || 'Medical Service'}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="h5" color="primary" fontWeight="bold">
                    ₹{formatAmount(linkData.amount || 0)}
                  </Typography>
                </Box>
              </Stack>
              
              {linkData.description && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {linkData.description}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Customer Details
            </Typography>
            
            <Stack spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Full Name *"
                value={customerDetails.name}
                onChange={(e) => setCustomerDetails({...customerDetails, name: e.target.value})}
                required
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Email Address *"
                  type="email"
                  value={customerDetails.email}
                  onChange={(e) => setCustomerDetails({...customerDetails, email: e.target.value})}
                  required
                />
                <TextField
                  fullWidth
                  label="Phone Number *"
                  value={customerDetails.phone}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    if (numericValue.length <= 10) {
                      setCustomerDetails({...customerDetails, phone: numericValue});
                    }
                  }}
                  required
                  placeholder="10 digit number"
                  inputProps={{ 
                    maxLength: 10,
                    inputMode: 'numeric' as const
                  }}
                  helperText={
                    customerDetails.phone.length === 10
                      ? '\u2713 Valid phone number'
                      : `${10 - customerDetails.phone.length} digits remaining`
                  }
                  error={customerDetails.phone.length > 0 && customerDetails.phone.length !== 10}
                  sx={{
                    '& .MuiFormHelperText-root': {
                      color: customerDetails.phone.length === 10 ? '#4CAF50' : '#FF9800'
                    }
                  }}
                />
              </Stack>
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Payment Method
            </Typography>
            
            <FormControl component="fieldset">
              <RadioGroup
                value={selectedPaymentMethod}
                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
              >
                <FormControlLabel 
                  value="upi" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PaymentIcon />
                      <span>UPI (PhonePe, Paytm, GPay, etc.)</span>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="card" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PaymentIcon />
                      <span>Credit/Debit Card</span>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="netbanking" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PaymentIcon />
                      <span>Net Banking</span>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="wallet" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PaymentIcon />
                      <span>Wallet</span>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Box sx={{ mt: 4 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handlePayment}
                disabled={loading || !customerDetails.name || !customerDetails.email || !customerDetails.phone}
                sx={{ py: 2 }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : (
                  `Pay ₹${formatAmount(linkData.amount || 0)}`
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Security Info */}
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Secure Payment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your payment is secured with industry-standard encryption
              </Typography>
            </Box>

            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  <LockIcon sx={{ mr: 1, fontSize: 16 }} />
                  128-bit SSL Encryption
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  All transactions are secured and encrypted
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 16 }} />
                  PCI DSS Compliant
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Meets highest security standards
                </Typography>
              </Box>

              <Chip 
                label="Powered by Razorpay" 
                variant="outlined" 
                size="small"
                sx={{ width: '100%' }}
              />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

export default PaymentLinkPage;